import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';

import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { portalOptions } from 'constants/dropdownOptions';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getCaseNumberValidation } from 'services/lead/leadServices';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import constants from 'constants/index';

import AsyncPaginate from 'components/AsyncPaginate/AsyncPaginate';
import { getGlobalDropdown } from 'services/caseLeadDropdownService';

const InformationDetails = (props) => {
  const {
    control,
    caseDetails,
    isCaseClosed,
    setValue,
    isLeadSpecific,
    caseStageList,
    errors,
    watch,
    practiceAreaUpdated,
    setPracticeAreaUpdated,
    caseStagePaginationToken,
  } = props;
  const userDetails = useUserDetailsContext();

  const practiceAreaId = watch('case_description.case_practice_area');

  const handlePortalChange = (e) => {
    if (e.value === 'enable') {
      setValue('case_details.is_create_client_portal', true);
    } else {
      setValue('case_details.is_create_client_portal', false);
    }
  };

  const validateCaseNumber = async (value) => {
    if (!Boolean(value)) {
      return true;
    }
    return new Promise(async (resolve) => {
      const response = await getCaseNumberValidation(value);
      if (response?.data?.length === 0 || response?.data?.[0]?.case_id === caseDetails?.case_id) {
        resolve(true);
      } else resolve('Duplicate Case Number');
    });
  };

  const newOptions = (list = []) => {
    return (
      list?.map((item) => ({
        ...item,
        isDisabled: item?.stage_name?.toLowerCase() === 'closed' || item?.value?.toLowerCase() === 'closed',
      })) || []
    );
  };

  const loadCaseStageOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;

    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const page = currentPage + 1;

    const data = {
      practice_area_id: [practiceAreaId],
    };

    const options = await getGlobalDropdown(
      '',
      'case_stage',
      inputValue,
      '',
      isLeadSpecific,
      page,
      pageLimit,
      '',
      '',
      practiceAreaId ? data : null
    );

    return {
      options: options?.data?.data || [],
      hasMore: options?.data?.has_next,
      additional: {
        pageLimit: pageLimit,
        page: page,
      },
    };
  };

  return (
    <div>
      <div className="caption-bold">Details</div>
      <Card className="card-bg mb-3">
        <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
          <div className="col-md-6 col-12 text-muted p-medium">Case Number</div>
          <div className="col-md-6 col-12 col-sm-7 d-flex align-items-center edit-field">
            <Controller
              name="case_description.case_number"
              control={control}
              rules={{ validate: { asyncValidation: validateCaseNumber } }}
              render={({ field }) => (
                <InputText
                  className="rounded flex-grow-1 w-75 input-shadow F-size14"
                  placeholder="Enter Case Number"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value.trimStart())}
                />
              )}
            />
          </div>
          <RequiredValidation field="case_description.case_number" message="Case Number Already Exist" errors={errors} />
        </div>
        {caseDetails && caseDetails.client && caseDetails.client.client_id ? (
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-6 col-12 text-muted p-medium">Clients Portal</div>
            <div className="col-md-6 col-12">
              <Controller
                name="is_create_client_portal"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    value={field.value ? 'enable' : 'disable'}
                    options={portalOptions}
                    onChange={(e) => handlePortalChange(e)}
                    placeholder="Select"
                    filter
                  />
                )}
              />
            </div>
          </div>
        ) : null}

        <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
          <div className="col-md-6 col-12 text-muted p-medium">Case Status</div>
          <div className="col-md-6 col-12 d-flex align-items-center edit-field">
            {<div className="w-100 h-100">{isCaseClosed ? 'Closed' : 'Open'}</div>}
          </div>
        </div>

        {isCaseClosed && !isLeadSpecific ? (
          <>
            <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
              <div className="col-md-6 col-12 text-muted p-medium">Case Closure Reason</div>
              <div className="col-md-6 col-12 d-flex align-items-center edit-field">
                {<div className="w-100 h-100">{caseDetails?.closure_reason ? caseDetails?.closure_reason : '-'}</div>}
              </div>
            </div>

            <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
              <div className="col-md-6 col-12 text-muted p-medium">Case Outcome</div>
              <div className="col-md-6 col-12 d-flex align-items-center edit-field">
                {<div className="w-100 h-100">{caseDetails?.outcome_reason ? caseDetails?.outcome_reason : '-'}</div>}
              </div>
            </div>
          </>
        ) : null}

        {isLeadSpecific && caseDetails?.is_open === false && (
          <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
            <div className="col-md-6 col-12 text-muted p-medium">Lead Closure Reason</div>
            <div className="col-md-6 col-12 d-flex align-items-center edit-field">
              {<div className="w-100 h-100">{caseDetails?.lead_close_reason ? caseDetails?.lead_close_reason : '-'}</div>}
            </div>
          </div>
        )}
        {isLeadSpecific && caseDetails?.is_open === false && caseDetails?.lead_close_reason === 'Referred Out' && (
          <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
            <div className="col-md-6 col-12 text-muted p-medium">Referred To</div>
            <div className="col-md-6 col-12 d-flex align-items-center edit-field min-w-0">
              <div className="w-100 h-100 text-break">
                {caseDetails?._ref_title_ || caseDetails?._ref_first_name_ || caseDetails?._ref_last_name_
                  ? `${caseDetails?._ref_title_ || ''} ${caseDetails?._ref_first_name_ || ''} ${caseDetails?._ref_last_name_ || ''}`.trim()
                  : '-'}
              </div>
            </div>
          </div>
        )}
        {isLeadSpecific && caseDetails?.is_open === false && caseDetails?.lead_close_reason === 'Referred Out' && (
          <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
            <div className="col-md-6 col-12 text-muted p-medium">Referred Out Reason</div>
            <div className="col-md-6 col-12 d-flex align-items-center edit-field">
              {<div className="w-100 h-100">{caseDetails?.referred_out_reason_id ? caseDetails?.referred_out_reason_id : '-'}</div>}
            </div>
          </div>
        )}

        {!isLeadSpecific && (
          <div className="d-flex align-items-center py-2 F-size14 flex-wrap ">
            <div className="col-md-6 col-12 text-muted p-medium">Case Stage</div>

            {userDetails?.getPermission('cases') === constants.permissions.WRITE && (
              <div className="col-md-6 col-12 d-flex align-items-center flex-direction-column edit-field">
                <div className="w-100">
                  {/* <Controller
                    name="case_description.case_stage"
                    control={control}
                    defaultValue={'Open'}
                    render={({ field }) => (
                      <Dropdown
                        value={field.value}
                        onChange={(e) => field.onChange(e.value)}
                        options={caseStageList}
                        className="flex-grow-1 w-100 input-shadow"
                        optionDisabled={(data) => data.label?.toLowerCase() === 'closed'}
                        placeholder="Select Case Stage"
                        filter
                        filterPlaceholder="Search"
                      />
                    )}
                  /> */}
                  <Controller
                    name="case_description.case_stage"
                    control={control}
                    defaultValue={'Open'}
                    render={({ field }) => {
                      return (
                        <AsyncPaginate
                          value={{ label: field.value, value: field.value }}
                          onChange={(selectedOption) =>
                            field.onChange(selectedOption?.value || selectedOption?.stage_name || selectedOption)
                          }
                          loadOptions={loadCaseStageOptions}
                          getOptionLabel={(option) => option?.label || option?.stage_name}
                          getOptionValue={(option) => option?.value || option?.case_stage_id}
                          additional={{
                            pageLimit: 15,
                            page: 0,
                          }}
                          className="flex-grow-1 w-100 input-shadow"
                          isOptionDisabled={(data) => data?.stage_name?.toLowerCase() === 'closed'}
                          placeholder="Select Case Stage"
                          filterPlaceholder="Search"
                          cacheUniqs={[practiceAreaId]}
                          styles={userDetails?.firmDetails?.appearance_preference}
                        />
                      );
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <div className="d-flex align-items-center py-2 F-size14 flex-wrap ">
          <div className="col-md-6 col-6 text-muted p-medium">Review Requested</div>
          <div className="col-md-6 col-6 d-flex align-items-center flex-direction-column edit-field">
            <div className="w-100">
              <Controller
                name="case_description.review_requested"
                control={control}
                render={({ field }) => (
                  <InputSwitch name={field.name} checked={field.value} onChange={(e) => field.onChange(e.value)} className="input-swich" />
                )}
              />
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center py-2 F-size14 flex-wrap ">
          <div className="col-md-6 col-6 text-muted p-medium">Review Received</div>
          <div className="col-md-6 col-6 d-flex align-items-center flex-direction-column edit-field">
            <div className="w-100">
              <Controller
                name="case_description.review_received"
                control={control}
                render={({ field }) => (
                  <InputSwitch name={field.name} checked={field.value} onChange={(e) => field.onChange(e.value)} className="input-swich" />
                )}
              />
            </div>
          </div>
        </div>
        {isLeadSpecific && (
          <div className="d-flex align-items-center py-2 F-size14 flex-wrap ">
            <div className="col-md-6 col-6 text-muted p-medium">Qualified Lead</div>
            <div className="col-md-6 col-6 d-flex align-items-center flex-direction-column edit-field">
              <div className="w-100">
                <Controller
                  name="case_description.is_qualified"
                  control={control}
                  render={({ field }) => (
                    <InputSwitch
                      name={field.name}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      className="input-swich"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        )}
        {isLeadSpecific && (
          <>
            <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
              <div className="col-md-6 col-6 text-muted p-medium">Lead Created</div>
              <div className="col-md-6 col-6 d-flex align-items-center flex-direction-column edit-field">
                <TimezoneDatePicker
                  monthNavigator
                  yearNavigator
                  yearRange={`${new Date().getFullYear() - 100}:${new Date().getFullYear() + 50}`}
                  showButtonBar
                  dateFormat={constants.date_format}
                  value={caseDetails?.lead_created_date || null}
                  className="rounded flex-grow-1 w-100 input-shadow"
                  disabled
                  placeholder="-"
                  timezone={userDetails?.userDetails?.timezone}
                />
              </div>
            </div>
            <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
              <div className="col-md-6 col-6 text-muted p-medium">Last Updated</div>
              <div className="col-md-6 col-6 d-flex align-items-center flex-direction-column edit-field">
                <TimezoneDatePicker
                  monthNavigator
                  yearNavigator
                  yearRange={`${new Date().getFullYear() - 100}:${new Date().getFullYear() + 50}`}
                  showButtonBar
                  dateFormat={constants.date_format}
                  value={caseDetails?.time || null}
                  className="rounded flex-grow-1 w-100 input-shadow"
                  disabled
                  placeholder="-"
                  timezone={userDetails?.userDetails?.timezone}
                />
              </div>
            </div>
            {caseDetails?.is_open === false && (
              <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
                <div className="col-md-6 col-6 text-muted p-medium">Date Closed</div>
                <div className="col-md-6 col-6 d-flex align-items-center flex-direction-column edit-field">
                  <TimezoneDatePicker
                    monthNavigator
                    yearNavigator
                    yearRange={`${new Date().getFullYear() - 100}:${new Date().getFullYear() + 50}`}
                    showButtonBar
                    dateFormat={constants.date_format}
                    value={caseDetails?.case_closed_date || null}
                    className="rounded flex-grow-1 w-100 input-shadow"
                    disabled
                    placeholder="-"
                    timezone={userDetails?.userDetails?.timezone}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default InformationDetails;
